/**
 * The scope switcher — `docs/scope-switcher.md`, from
 * `26-scope-switcher/html/scope-main.html`.
 *
 * What it replaces was a flat list: branches first, then the company and the
 * regions in no order, plain checkboxes, no hierarchy, and a top row that
 * promised everything in a sentence. Ticking a region ticked nothing under it,
 * and
 * even if it had, the server passed the region's id straight into every list
 * query, where it matched no row, because records live on branches. The most
 * useful pick in the control silently showed an empty page.
 *
 * So this is the organization tree, in the same order and wearing the same
 * kind tiles as Settings › Organization, with ticks that cascade. The rules
 * are in `shared/src/scope.ts` and the server reads the same ones, which is
 * what makes "East Coast" mean East Coast on both sides of the wire.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  countAt, coveredBranches, normalise, only, scopeLabelOf, stateOf, toggle,
  type MeResponse, type OrgUnitSummary, type ScopeTree,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { Popover } from '../components/overlays.js'
import { Button, Checkbox } from '../components/primitives.js'
import { KindTile } from '../builder/kind-tile.js'
import { IconCheck, IconChevronDown, IconChevronRight, IconSearch } from './icons.js'
/*
 * The tree itself is `scope/scope-tree.tsx` — shared with the branch field a
 * record carries (`docs/services-and-branch-scope.md` §3), so the two draw the
 * same rows rather than two things that look alike until one is changed.
 */
import {
  NoMatch, ScopeRow, defaultOpen, metaOf, offerable, plural, rowsOf, treeOf,
  type Row,
} from '../scope/scope-tree.js'

const RECENT_LIMIT = 3

interface SwitcherProps {
  me: MeResponse
  open: boolean
  onToggle: () => void
  onClose: () => void
  /** Applies the selection: the roots, not the branches under them. */
  onApply: (roots: string[]) => void
}

export function ScopeSwitcher({ me, open, onToggle, onClose, onApply }: SwitcherProps) {
  /*
   * The ref goes on the wrapper, not the button: the wrapper's box *is* the
   * button's box, and this saves a `forwardRef` on a component that exists
   * only to be that button.
   */
  const at = useRef<HTMLDivElement>(null)
  const phone = usePhone()

  return (
    <div ref={at} className="relative min-w-0 shrink">
      <ScopeTrigger me={me} open={open} onClick={onToggle} />
      {open && (phone
        ? <ScopeSheet me={me} onClose={onClose} onApply={onApply} />
        : <ScopePanel me={me} anchor={at.current} onClose={onClose} onApply={onApply} />)}
    </div>
  )
}

/**
 * The header button — §1.4. It names places, never a sentence.
 *
 * The tile says what kind of place it is, which is why the label never has to:
 * "Harrisburg", not "Harrisburg branch", and the pin icon that used to sit
 * here said nothing at all.
 */
const ScopeTrigger = ({ me, open, onClick }: {
  me: MeResponse
  open: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-haspopup="dialog"
    title={me.scope.label}
    className={cn(
      'inline-flex h-[34px] max-w-[260px] items-center gap-2 rounded-button py-0 pl-1.5 pr-2.5',
      'text-body font-semibold text-text hover:bg-hover',
      open && 'bg-hover',
    )}
  >
    <KindTile kind={me.scope.labelKind} size={22} />
    <span className="hidden truncate sm:inline">{me.scope.label}</span>
    <span className="shrink-0 text-text-faint"><IconChevronDown size={13} /></span>
  </button>
)

/** Everything both surfaces need: a draft selection and the rules over it. */
function useDraft(me: MeResponse) {
  /*
   * On when what is already selected is a closed place, so somebody looking
   * at a closed branch's jobs can see *why* the header says what it says.
   */
  const [showClosed, setShowClosed] = useState(() => me.scope.roots.some((id) =>
    me.scope.available.find((unit) => unit.id === id)?.closed))

  const offered = useMemo(
    () => offerable(me.scope.available, showClosed), [me.scope.available, showClosed])
  const everywhere = useMemo(() => treeOf(me.scope.available), [me.scope.available])
  const closed = useMemo(
    () => me.scope.available.filter((unit) =>
      unit.kind === 'branch' && unit.reachable && unit.closed),
    [me.scope.available])
  const closedCount = closed.length

  const tree = useMemo(() => treeOf(offered), [offered])
  const facts = useMemo(
    () => new Map(offered.map((unit) => [unit.id, unit])), [offered])

  const [roots, setRoots] = useState<string[]>(() => normalise(me.scope.roots, tree))
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<Set<string>>(() => defaultOpen(me.scope.roots, tree))

  const rows = useMemo(() => rowsOf(tree, facts, open, query), [tree, facts, open, query])
  const covered = useMemo(() => coveredBranches(roots, tree), [roots, tree])
  const label = useMemo(() => scopeLabelOf(roots, tree).text, [roots, tree])

  const toggleOpen = useCallback((id: string) => setOpen((current) => {
    const next = new Set(current)
    if (!next.delete(id)) next.add(id)
    return next
  }), [])

  /*
   * Showing the closed branches opens the way to them.
   *
   * Their district usually starts collapsed — it is fully selected, so there
   * was nothing in it worth unfolding — and a button that reveals a row two
   * levels inside a folded group is a button that looks broken.
   */
  const revealClosed = useCallback((on: boolean) => {
    setShowClosed(on)
    if (!on) return
    setOpen((current) => {
      const next = new Set(current)
      for (const unit of closed) for (const up of everywhere.ancestorsOf(unit.id)) next.add(up)
      return next
    })
  }, [closed, everywhere])

  const setOpenAt = useCallback((id: string, wanted: boolean) => setOpen((current) => {
    if (current.has(id) === wanted) return current
    const next = new Set(current)
    if (wanted) next.add(id)
    else next.delete(id)
    return next
  }), [])

  return {
    tree, facts, roots, setRoots, query, setQuery, rows, covered, label, toggleOpen, setOpenAt,
    showClosed, setShowClosed: revealClosed, closedCount,
  }
}

/** The recents chips, and the server-side list behind them. */
function useRecents(me: MeResponse) {
  const [recent, setRecent] = useState<string[]>([])

  useEffect(() => {
    let live = true
    request<{ orgUnitIds: string[] }>('/me/scope/recent')
      .then((payload) => { if (live) setRecent(payload.orgUnitIds) })
      /* A missing shortcut is not worth an error; the tree is right there. */
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  const remember = useCallback((roots: string[]) => {
    const next = [...roots, ...recent].filter((id, index, all) => all.indexOf(id) === index)
      .slice(0, RECENT_LIMIT)
    void request('/me/scope/recent', { method: 'PUT', body: { orgUnitIds: next } })
      .catch(() => undefined)
  }, [recent])

  const chips = recent
    .map((id) => me.scope.available.find((unit) => unit.id === id))
    .filter((unit): unit is OrgUnitSummary => Boolean(unit?.reachable))

  return { chips, remember }
}

/** 380 wide, under the trigger (§3.2). */
function ScopePanel({ me, anchor, onClose, onApply }: {
  me: MeResponse
  anchor: HTMLElement | null
  onClose: () => void
  onApply: (roots: string[]) => void
}) {
  const draft = useDraft(me)
  const { chips, remember } = useRecents(me)
  const search = useRef<HTMLInputElement>(null)
  /*
   * -1 until somebody arrows. A panel that opens with its first row already
   * highlighted reads as "this one is selected", which it is not — the ticks
   * say what is selected. The mouse gets `hover:` and nothing else.
   */
  const [focus, setFocus] = useState(-1)

  const apply = (roots: string[]) => {
    if (roots.length === 0) return
    remember(roots)
    onApply(roots)
  }

  const keys = useKeyboard({
    draft, focus, setFocus, search, onApply: () => apply(draft.roots), onClose,
  })

  return (
    <Popover
      open
      onClose={onClose}
      anchor={anchor}
      width={380}
      align="left"
      className="rounded-menu"
      label="Where are you working"
    >
      <div onKeyDown={keys} role="presentation">
        <div className="px-3 pb-1.5 pt-2.5">
          <span className={cn(
            'flex h-[34px] w-full items-center gap-2 rounded-control border border-line',
            'bg-surface pl-2.5 pr-2 text-cell text-text',
          )}>
            <IconSearch size={14} />
            <input
              ref={search}
              autoFocus
              value={draft.query}
              onChange={(event) => { draft.setQuery(event.target.value); setFocus(-1) }}
              placeholder="Find a branch, district or region"
              aria-label="Find a branch, district or region"
              className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-text-faint"
            />
            <kbd className={cn(
              'rounded-tag border border-line px-1.5 py-px text-tag text-text-faint',
            )}>/</kbd>
          </span>
        </div>

        {chips.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-hidden px-3 pb-2">
            <span className="mr-0.5 shrink-0 text-sub text-text-faint">Recent</span>
            {chips.map((unit) => (
              <button
                key={unit.id}
                type="button"
                onClick={() => apply(only(unit.id))}
                className={cn(
                  'inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-chip border',
                  'border-line bg-surface pl-[5px] pr-[9px] text-chip font-semibold',
                  'text-text-body hover:bg-hover',
                )}
              >
                <KindTile kind={unit.kind} size={16} />
                <span className="truncate">{unit.name}</span>
              </button>
            ))}
          </div>
        )}

        <div
          role="listbox"
          aria-label="Places"
          className="max-h-[440px] overflow-y-auto border-t border-line-faint py-1.5"
        >
          {draft.rows.length === 0
            ? <NoMatch query={draft.query} />
            : draft.rows.map((row, index) => (
              <ScopeRow
                key={row.unit.id}
                row={row}
                tree={draft.tree}
                roots={draft.roots}
                focused={index === focus}
                onToggle={() => draft.setRoots(toggle(row.unit.id, draft.roots, draft.tree))}
                onToggleOpen={() => draft.toggleOpen(row.unit.id)}
                onOnly={() => draft.setRoots(only(row.unit.id))}
              />
            ))}

          <ClosedRow draft={draft} />
        </div>

        <div className={cn(
          'flex items-center gap-2 border-t border-line bg-surface-alt px-3 py-2.5',
        )}>
          <span className="min-w-0 truncate text-cell text-text-muted">
            {draft.covered.length} of {plural(draft.tree.allBranches.length, 'branch')}
            {draft.label ? ` · ${draft.label}` : ''}
          </span>
          <span className="ml-auto flex gap-2">
            <Button onClick={() => draft.setRoots([])}>Clear</Button>
            <Button
              variant="primary"
              disabled={draft.roots.length === 0}
              onClick={() => apply(draft.roots)}
            >
              <IconCheck size={15} />
              Apply
            </Button>
          </span>
        </div>
      </div>
    </Popover>
  )
}

/**
 * The way back to a branch that has closed.
 *
 * One row at the foot of the tree, and only when there is one to show. A
 * permanent "include closed" switch would be chrome every company pays for so
 * that the few who have closed a branch can find it.
 */
function ClosedRow({ draft }: { draft: ReturnType<typeof useDraft> }) {
  if (draft.closedCount === 0) return null

  return (
    <button
      type="button"
      onClick={() => draft.setShowClosed(!draft.showClosed)}
      className={cn('mx-1.5 mt-0.5 flex h-8 items-center gap-1.5 rounded-control pl-[10px]',
        'pr-2.5 text-chip font-semibold text-text-muted hover:bg-hover')}
    >
      {draft.showClosed ? 'Hide' : 'Show'} {plural(draft.closedCount, 'closed branch')}
    </button>
  )
}

/**
 * `/` · ↑↓ · space · →← · `o` · ⏎ · Esc — §3.2.
 *
 * A tree of forty rows is a scroll on a trackpad and four keystrokes for
 * somebody who lives in it, and the people who switch branch twenty times a
 * day are the second kind.
 */
function useKeyboard({ draft, focus, setFocus, search, onApply, onClose }: {
  draft: ReturnType<typeof useDraft>
  focus: number
  setFocus: (index: number) => void
  search: React.RefObject<HTMLInputElement | null>
  onApply: () => void
  onClose: () => void
}) {
  return (event: React.KeyboardEvent) => {
    const row = draft.rows[focus]
    const typing = event.target === search.current

    if (event.key === '/' && !typing) {
      event.preventDefault()
      search.current?.focus()
      return
    }
    if (event.key === 'Escape') { onClose(); return }
    if (event.key === 'Enter') { event.preventDefault(); onApply(); return }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setFocus(Math.min(focus + 1, draft.rows.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setFocus(Math.max(focus - 1, focus <= 0 ? -1 : 0))
      return
    }

    if (!row) return

    if (event.key === ' ' && !typing) {
      event.preventDefault()
      draft.setRoots(toggle(row.unit.id, draft.roots, draft.tree))
      return
    }
    if (event.key === 'ArrowRight' && !typing) {
      event.preventDefault()
      draft.setOpenAt(row.unit.id, true)
      return
    }
    if (event.key === 'ArrowLeft' && !typing) {
      event.preventDefault()
      if (row.open && row.hasChildren) { draft.setOpenAt(row.unit.id, false); return }
      const up = draft.rows.findIndex((other) => other.unit.id === row.unit.parentId)
      if (up >= 0) setFocus(up)
      return
    }
    if (event.key.toLowerCase() === 'o' && !typing) {
      event.preventDefault()
      draft.setRoots(only(row.unit.id))
    }
  }
}

/**
 * The phone — a full-screen sheet with the same tree at 48px rows.
 *
 * Not the record `Sheet`: that one is the shell for "New …" and "Edit …", 620
 * from the right with a kind tile, a path and a dirty check. This is a picker,
 * and the mockup draws it as the platform draws a picker — Cancel and Apply in
 * the bar, and a primary at the bottom that says what it will do rather than
 * "Save".
 */
function ScopeSheet({ me, onClose, onApply }: {
  me: MeResponse
  onClose: () => void
  onApply: (roots: string[]) => void
}) {
  const draft = useDraft(me)
  const { remember } = useRecents(me)

  const apply = () => {
    if (draft.roots.length === 0) return
    remember(draft.roots)
    onApply(draft.roots)
  }

  // Escape is Cancel, as it is on the desktop panel: a dialog that covers the
  // whole screen must still let a keyboard out of it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Where are you working"
      className="fixed inset-0 z-50 flex flex-col bg-bg"
    >
      <div className={cn(
        'flex items-center gap-2.5 border-b border-line bg-surface px-4 pb-2.5 pt-1.5',
      )}>
        <button type="button" onClick={onClose}
          className="text-[15px] font-semibold text-accent">
          Cancel
        </button>
        <span className="flex flex-1 flex-col items-center gap-px">
          <b className="text-[17px]">Where</b>
          <span className="text-cell text-text-muted">
            {draft.covered.length} of {plural(draft.tree.allBranches.length, 'branch')}
          </span>
        </span>
        <button type="button" onClick={apply} className="text-[15px] font-bold text-accent">
          Apply
        </button>
      </div>

      <div className="px-4 pb-1.5 pt-3">
        <span className={cn(
          'flex h-10 w-full items-center gap-2 rounded-field border border-line bg-surface',
          'px-3 text-[14px] text-text',
        )}>
          <IconSearch size={15} />
          <input
            value={draft.query}
            onChange={(event) => draft.setQuery(event.target.value)}
            placeholder="Find a branch, district or region"
            aria-label="Find a branch, district or region"
            className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-text-faint"
          />
        </span>
      </div>

      <div role="listbox" aria-label="Places" className="min-h-0 flex-1 overflow-y-auto">
        {draft.rows.length === 0
          ? <NoMatch query={draft.query} />
          : draft.rows.map((row) => (
            <SheetRow
              key={row.unit.id}
              row={row}
              tree={draft.tree}
              roots={draft.roots}
              onToggle={() => draft.setRoots(toggle(row.unit.id, draft.roots, draft.tree))}
              onToggleOpen={() => draft.toggleOpen(row.unit.id)}
            />
          ))}
      </div>

      <div className="border-t border-line bg-surface px-4 pb-6 pt-3">
        <Button
          variant="primary"
          disabled={draft.roots.length === 0}
          onClick={apply}
          className="h-[52px] w-full justify-center rounded-popup text-[15px]"
        >
          <IconCheck size={15} />
          Show {plural(draft.covered.length, 'branch')}
        </Button>
      </div>
    </div>,
    document.body,
  )
}

function SheetRow({ row, tree, roots, onToggle, onToggleOpen }: {
  row: Row
  tree: ScopeTree
  roots: string[]
  onToggle: () => void
  onToggleOpen: () => void
}) {
  const { unit, fact, depth, hasChildren, open } = row
  const state = stateOf(unit.id, roots, tree)
  const count = countAt(unit.id, roots, tree)
  const disabled = !unit.reachable || count.total === 0

  return (
    <div
      role="option"
      aria-selected={state === 'on'}
      className={cn('flex h-12 items-center gap-2.5 border-b border-line-faint bg-surface pr-4',
        disabled && 'opacity-50')}
      style={{ paddingLeft: 16 + 18 * depth }}
      onClick={() => { if (!disabled) onToggle() }}
    >
      <Checkbox
        checked={state === 'on'}
        indeterminate={state === 'mixed'}
        disabled={disabled}
        onChange={() => { if (!disabled) onToggle() }}
        onClick={(event) => event.stopPropagation()}
        label={unit.name}
        hideLabel
      />
      <KindTile kind={unit.kind} size={30} />
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <b className="truncate text-[14px]">{unit.name}</b>
        <span className="truncate text-cell text-text-muted">{metaOf(unit, fact, tree)}</span>
      </span>
      {unit.kind !== 'branch' && count.total > 0 && (
        <span className={cn('shrink-0 text-cell tabular-nums',
          state === 'on' ? 'text-accent' : 'text-text-faint')}>
          {count.on} of {count.total}
        </span>
      )}
      {hasChildren && (
        <button
          type="button"
          aria-label={open ? `Collapse ${unit.name}` : `Expand ${unit.name}`}
          onClick={(event) => { event.stopPropagation(); onToggleOpen() }}
          className="shrink-0 text-text-faint"
        >
          {open ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
        </button>
      )}
    </div>
  )
}
